// src/pages/quotations/QuotationCreate.jsx

import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import {
  createQuotation, updateQuotation, fetchQuotation,
  calculateTotals, BOQ_SECTIONS, BOQ_UNITS,
} from "../../services/quotationService";
import {
  fetchClientsWithSites, fetchSitesForClient, autoSelectSiteIfOnlyOne,
} from "../../services/clientService";

const PROJECT_TYPES = [
  { val: "vrf",         label: "VRF/VRV System" },
  { val: "chiller",     label: "Chiller System" },
  { val: "ducted",      label: "Ducted AC" },
  { val: "split",       label: "Split/Cassette AC" },
  { val: "precision",   label: "Precision AC/Server Room" },
  { val: "ventilation", label: "Ventilation/AHU" },
  { val: "supply",      label: "Supply Only" },
  { val: "amc",         label: "AMC" },
  { val: "other",       label: "Other" },
];

const emptyItem = (section = "material") => ({
  section, item_no: 1, item_name: "", brand: "", model: "",
  hsn_code: "",
  unit: "Nos", qty: 1, rate: 0, amount: 0, is_optional: false, remarks: "",
});

export default function QuotationCreate() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";

  const editId = new URLSearchParams(location.search).get("id");

  const [saving,   setSaving]   = useState(false);
  const [clients,  setClients]  = useState([]);
  const [sites,    setSites]    = useState([]);

  const [form, setForm] = useState({
    client_id:        "",
    client_name:      "",
    client_phone:     "",
    client_email:     "",
    site_id:          "",
    site_name:        "",
    project_type:     "vrf",
    subject:          "",
    validity_days:    30,
    validity_date:    "",
    payment_terms:    "40% advance, 50% on completion, 10% retention",
    terms_conditions: "",
    notes:            "",
    discount:         0,
    tax_percent:      18,
  });

  const [boqMode,       setBoqMode]       = useState("manual"); // manual | upload
  const [boqFile,       setBoqFile]       = useState(null);
  const [boqUploadTotal, setBoqUploadTotal] = useState("");
  const [boqUploadDesc,  setBoqUploadDesc]  = useState("");
  const boqFileRef = useRef(null);

  const [items, setItems] = useState([
    { ...emptyItem("material"),     item_no: 1 },
    { ...emptyItem("installation"), item_no: 1 },
  ]);

  useEffect(() => {
    fetchClients();
    if (editId) loadForEdit(editId);
    // Pre-fill from enquiry/project
    if (location.state?.from_enquiry) {
      const s = location.state;
      setForm(f => ({
        ...f,
        client_id:   s.client_id   || "",
        client_name: s.client_name || "",
        site_id:     s.site_id     || "",
        site_name:   s.site_name   || "",
        subject:     s.subject     || "",
      }));
      if (s.client_id) fetchSites(s.client_id);
    }
  }, []); // eslint-disable-line

  const loadForEdit = async (id) => {
    try {
      const q = await fetchQuotation(id, company_id);
      setForm({
        client_id:        q.client_id        || "",
        client_name:      q.client_name      || "",
        client_phone:     q.client_phone     || "",
        client_email:     q.client_email     || "",
        site_id:          q.site_id          || "",
        site_name:        q.site_name        || "",
        project_type:     q.project_type     || "vrf",
        subject:          q.subject          || "",
        validity_days:    q.validity_days    || 30,
        validity_date:    q.validity_date    || "",
        payment_terms:    q.payment_terms    || "",
        terms_conditions: q.terms_conditions || "",
        notes:            q.notes            || "",
        discount:         q.discount         || 0,
        tax_percent:      q.tax_percent      || 18,
      });
      if (q.items?.length) setItems(q.items);
      if (q.client_id) fetchSites(q.client_id);
    } catch (err) { alert("Failed to load quotation"); }
  };

  // Only clients that have at least one site (SOT check) are shown.
  const fetchClients = async () => {
    setClients(await fetchClientsWithSites(company_id));
  };

  // Auto-selects the site (and phone/email stay as-is) if the client has
  // exactly one site — saves the operator a step for the common case.
  const fetchSites = async (clientId) => {
    const list = await fetchSitesForClient(company_id, clientId);
    setSites(list);

    const onlySite = autoSelectSiteIfOnlyOne(list);
    if (onlySite) {
      setForm(f => ({ ...f, site_id: onlySite.id, site_name: onlySite.site_name || onlySite.site_title }));
    }
  };

  // ── Auto validity date ────────────────────────────────────────────────────
  const handleValidityDays = (days) => {
    const d = new Date();
    d.setDate(d.getDate() + Number(days));
    setForm(f => ({
      ...f,
      validity_days: days,
      validity_date: d.toISOString().split("T")[0],
    }));
  };

  // ── Item helpers ──────────────────────────────────────────────────────────
  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    updated[index].amount = Number(updated[index].qty) * Number(updated[index].rate);
    setItems(updated);
  };

  const addItem = (section) => {
    const sectionItems = items.filter(i => i.section === section);
    setItems([...items, {
      ...emptyItem(section),
      item_no: sectionItems.length + 1,
    }]);
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  // ── Totals ────────────────────────────────────────────────────────────────
  const { subtotal, tax_amount, total_amount } = calculateTotals(
    items, form.discount, form.tax_percent
  );

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async (status = "draft") => {
    if (!form.client_name.trim()) { alert("Client required"); return; }
    if (!form.subject.trim())     { alert("Subject required"); return; }
    if (boqMode === "upload" && !boqUploadTotal) {
      alert("BOQ total amount enter karo"); return;
    }

    try {
      setSaving(true);

      // If upload mode — create single line item from total
      const finalItems = boqMode === "upload"
        ? [{
            section:   "material",
            item_no:   1,
            item_name: boqUploadDesc || "BOQ — As per attached document",
            brand:     "",
            model:     "",
            unit:      "LS",
            qty:       1,
            rate:      Number(boqUploadTotal),
            amount:    Number(boqUploadTotal),
            is_optional: false,
            remarks:   "Refer attached BOQ document",
          }]
        : items;

      const { subtotal: fs, tax_amount: ft, total_amount: fTotal } =
        boqMode === "upload"
          ? { subtotal: Number(boqUploadTotal),
              tax_amount: Math.round((Number(boqUploadTotal) - form.discount) * form.tax_percent / 100),
              total_amount: Math.round((Number(boqUploadTotal) - form.discount) * (1 + form.tax_percent / 100)) }
          : { subtotal, tax_amount, total_amount };

      const payload = {
        ...form,
        items:         finalItems,
        boq_mode:      boqMode,
        boq_file_name: boqFile?.name || "",
        subtotal:      fs,
        tax_amount:    ft,
        total_amount:  fTotal,
        status,
      };

      if (editId) {
        await updateQuotation(editId, payload, company_id);
        alert("Quotation updated ✅");
        navigate(`/quotations/${editId}`);
      } else {
        const id = await createQuotation(payload, user);
        alert(status === "sent"
          ? "Quotation saved & marked as Sent ✅"
          : "Quotation saved as Draft ✅");
        navigate(`/quotations/${id}`);
      }
    } catch (err) {
      alert("Failed to save: " + err.message);
    } finally { setSaving(false); }
  };

  // ── Items by section ──────────────────────────────────────────────────────
  const itemsBySection = (section) => items
    .map((item, idx) => ({ ...item, _idx: idx }))
    .filter(item => item.section === section);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto pb-20">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <button onClick={() => navigate("/quotations")}
            className="text-gray-400 text-sm hover:text-gray-600 mb-1 block">
            ← Quotations
          </button>
          <h1 className="text-2xl font-bold text-gray-800">
            {editId ? "Edit Quotation" : "New Quotation"}
          </h1>
        </div>
      </div>

      {/* CLIENT + PROJECT INFO */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 space-y-4">
        <h2 className="font-semibold text-gray-700">Client & Project Info</h2>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Client *</label>
            <select
              value={form.client_id}
              onChange={e => {
                const c = clients.find(c => c.id === e.target.value);
                if (!c) return;
                setForm(f => ({ ...f, client_id: c.id, client_name: c.client_name,
                  client_phone: c.contact || "", site_id: "", site_name: "" }));
                fetchSites(c.id);
              }}
              className="border p-2 rounded-xl w-full text-sm outline-none bg-white">
              <option value="">Select Client</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Site</label>
            <select
              value={form.site_id}
              onChange={e => {
                const s = sites.find(s => s.id === e.target.value);
                if (!s) return;
                setForm(f => ({ ...f, site_id: s.id, site_name: s.site_name || s.site_title }));
              }}
              disabled={!form.client_id}
              className="border p-2 rounded-xl w-full text-sm outline-none bg-white disabled:opacity-50">
              <option value="">Select Site</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.site_name || s.site_title}</option>)}
            </select>
            {sites.length === 1 && form.site_id === sites[0].id && (
              <p className="text-xs text-green-600 mt-1 font-medium">
                ✓ Auto-selected (only site for this client)
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Client Phone</label>
            <input value={form.client_phone}
              onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))}
              placeholder="Phone number"
              className="border p-2 rounded-xl w-full text-sm outline-none" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Client Email</label>
            <input value={form.client_email}
              onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))}
              placeholder="Email address"
              className="border p-2 rounded-xl w-full text-sm outline-none" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Project Type</label>
            <select value={form.project_type}
              onChange={e => setForm(f => ({ ...f, project_type: e.target.value }))}
              className="border p-2 rounded-xl w-full text-sm outline-none bg-white">
              {PROJECT_TYPES.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Subject *</label>
            <input value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="e.g. Supply & Installation of VRF System"
              className="border p-2 rounded-xl w-full text-sm outline-none" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Validity</label>
            <div className="flex gap-2">
              <select
                value={form.validity_days}
                onChange={e => handleValidityDays(e.target.value)}
                className="border p-2 rounded-xl text-sm outline-none bg-white w-32">
                {[15, 30, 45, 60, 90].map(d => (
                  <option key={d} value={d}>{d} days</option>
                ))}
              </select>
              <input type="date" value={form.validity_date}
                onChange={e => setForm(f => ({ ...f, validity_date: e.target.value }))}
                className="border p-2 rounded-xl text-sm outline-none flex-1" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Payment Terms</label>
            <input value={form.payment_terms}
              onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))}
              className="border p-2 rounded-xl w-full text-sm outline-none" />
          </div>
        </div>
      </div>

      {/* BOQ MODE TOGGLE */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
        <h2 className="font-semibold text-gray-700 mb-3">BOQ Method</h2>
        <div className="flex gap-3">
          <button
            onClick={() => setBoqMode("manual")}
            className={`flex-1 py-3 rounded-xl text-sm font-semibold border transition ${
              boqMode === "manual"
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-600 border-gray-200"
            }`}>
            📝 Build BOQ Manually
          </button>
          <button
            onClick={() => setBoqMode("upload")}
            className={`flex-1 py-3 rounded-xl text-sm font-semibold border transition ${
              boqMode === "upload"
                ? "bg-green-600 text-white border-green-600"
                : "bg-white text-gray-600 border-gray-200"
            }`}>
            📎 Upload BOQ File
          </button>
        </div>

        {boqMode === "upload" && (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-gray-500">
              Upload existing BOQ — Excel, PDF, or Word format
            </p>
            <input
              ref={boqFileRef}
              type="file"
              accept=".pdf,.xlsx,.xls,.doc,.docx"
              onChange={e => setBoqFile(e.target.files[0] || null)}
              className="w-full border border-dashed border-green-300 rounded-xl p-3 text-sm text-gray-600"
            />
            {boqFile && (
              <p className="text-xs text-green-600">
                ✅ {boqFile.name} — will be saved with quotation
              </p>
            )}
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
              ⚠️ Uploaded BOQ will be stored as document.
              Totals enter karo manually neeche.
            </p>

            {/* Manual total entry for uploaded BOQ */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">
                  BOQ Total Amount ₹ *
                </label>
                <input
                  type="number"
                  placeholder="Enter total from your BOQ"
                  value={boqUploadTotal}
                  onChange={e => setBoqUploadTotal(e.target.value)}
                  className="border p-2 rounded-xl w-full text-sm outline-none focus:ring-2 focus:ring-green-300"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">
                  Description
                </label>
                <input
                  placeholder="e.g. VRF System Supply & Installation"
                  value={boqUploadDesc}
                  onChange={e => setBoqUploadDesc(e.target.value)}
                  className="border p-2 rounded-xl w-full text-sm outline-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* BOQ — SECTIONS — only show in manual mode */}
      {boqMode === "manual" && Object.entries(BOQ_SECTIONS).map(([sectionKey, sectionInfo]) => {
        const sectionItems = itemsBySection(sectionKey);
        const sectionTotal = sectionItems.reduce((s, i) => s + Number(i.amount || 0), 0);

        return (
          <div key={sectionKey} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="text-lg">{sectionInfo.icon}</span>
                <span className="font-semibold text-gray-700">{sectionInfo.label}</span>
                <span className="text-xs text-gray-400">({sectionItems.length} items)</span>
              </div>
              <span className="text-sm font-bold text-gray-700">
                ₹{sectionTotal.toLocaleString("en-IN")}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left w-6">#</th>
                    <th className="p-2 text-left">Item Name</th>
                    <th className="p-2 text-left w-24">Brand</th>
                    <th className="p-2 text-left w-24">Model</th>
                    <th className="p-2 text-left w-20">HSN Code</th>
                    <th className="p-2 text-left w-16">Unit</th>
                    <th className="p-2 text-right w-16">Qty</th>
                    <th className="p-2 text-right w-24">Rate ₹</th>
                    <th className="p-2 text-right w-24">Amount ₹</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sectionItems.map((item, i) => (
                    <tr key={i} className={item.is_optional ? "bg-yellow-50" : ""}>
                      <td className="p-1.5 text-center text-xs text-gray-400">{i + 1}</td>
                      <td className="p-1.5">
                        <input
                          value={item.item_name}
                          onChange={e => updateItem(item._idx, "item_name", e.target.value)}
                          placeholder="Item description..."
                          className="w-full border border-gray-100 rounded-lg p-1.5 text-xs outline-none focus:ring-1 focus:ring-blue-300 min-w-[180px]"
                        />
                      </td>
                      <td className="p-1.5">
                        <input
                          value={item.brand}
                          onChange={e => updateItem(item._idx, "brand", e.target.value)}
                          placeholder="Brand"
                          className="w-full border border-gray-100 rounded-lg p-1.5 text-xs outline-none focus:ring-1 focus:ring-blue-300"
                        />
                      </td>
                      <td className="p-1.5">
                        <input
                          value={item.model}
                          onChange={e => updateItem(item._idx, "model", e.target.value)}
                          placeholder="Model"
                          className="w-full border border-gray-100 rounded-lg p-1.5 text-xs outline-none focus:ring-1 focus:ring-blue-300"
                        />
                      </td>
                      <td className="p-1.5">
                        <input
                          value={item.hsn_code || ""}
                          onChange={e => updateItem(item._idx, "hsn_code", e.target.value)}
                          placeholder="HSN"
                          className="w-full border border-gray-100 rounded-lg p-1.5 text-xs outline-none focus:ring-1 focus:ring-blue-300"
                        />
                      </td>
                      <td className="p-1.5">
                        <select
                          value={item.unit}
                          onChange={e => updateItem(item._idx, "unit", e.target.value)}
                          className="w-full border border-gray-100 rounded-lg p-1.5 text-xs outline-none bg-white">
                          {BOQ_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="p-1.5">
                        <input type="number" min="0"
                          value={item.qty}
                          onChange={e => updateItem(item._idx, "qty", e.target.value)}
                          className="w-full border border-gray-100 rounded-lg p-1.5 text-xs outline-none text-right focus:ring-1 focus:ring-blue-300"
                        />
                      </td>
                      <td className="p-1.5">
                        <input type="number" min="0"
                          value={item.rate}
                          onChange={e => updateItem(item._idx, "rate", e.target.value)}
                          className="w-full border border-gray-100 rounded-lg p-1.5 text-xs outline-none text-right focus:ring-1 focus:ring-blue-300"
                        />
                      </td>
                      <td className="p-1.5 text-right font-semibold text-xs">
                        ₹{Number(item.amount || 0).toLocaleString("en-IN")}
                      </td>
                      <td className="p-1.5 text-center">
                        <button
                          onClick={() => removeItem(item._idx)}
                          className="text-gray-300 hover:text-red-500 text-sm">✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-2 flex items-center gap-3">
              <button
                onClick={() => addItem(sectionKey)}
                className="text-xs text-blue-600 font-semibold hover:underline">
                + Add {sectionInfo.label} Item
              </button>
            </div>
          </div>
        );
      })}

      {/* TOTALS + TERMS — always show */}
      <div className="grid md:grid-cols-2 gap-5">

        {/* Terms */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 space-y-3">
          <h2 className="font-semibold text-gray-700">Terms & Notes</h2>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Terms & Conditions</label>
            <textarea rows={3}
              value={form.terms_conditions}
              onChange={e => setForm(f => ({ ...f, terms_conditions: e.target.value }))}
              placeholder="Standard terms..."
              className="border p-2 rounded-xl w-full text-sm resize-none outline-none" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Notes</label>
            <textarea rows={2}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Additional notes..."
              className="border p-2 rounded-xl w-full text-sm resize-none outline-none" />
          </div>
        </div>

        {/* Totals */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 space-y-3">
          <h2 className="font-semibold text-gray-700">Summary</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Subtotal</span>
              <span className="font-semibold">₹{subtotal.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Discount ₹</span>
              <input type="number" min="0"
                value={form.discount}
                onChange={e => setForm(f => ({ ...f, discount: Number(e.target.value) }))}
                className="border p-1 rounded-lg text-xs w-24 text-right outline-none" />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Tax %</span>
              <input type="number" min="0" max="100"
                value={form.tax_percent}
                onChange={e => setForm(f => ({ ...f, tax_percent: Number(e.target.value) }))}
                className="border p-1 rounded-lg text-xs w-24 text-right outline-none" />
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>Tax Amount</span>
              <span>₹{tax_amount.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between font-bold text-base border-t pt-2">
              <span>Total</span>
              <span className="text-green-700">₹{total_amount.toLocaleString("en-IN")}</span>
            </div>
          </div>

          {/* Save Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => handleSave("draft")}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold text-sm disabled:opacity-50">
              {saving ? "Saving..." : "Save Draft"}
            </button>
            <button
              onClick={() => handleSave("sent")}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-50">
              {saving ? "Saving..." : "Save & Mark Sent"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}